import { Injectable, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, EMPTY } from 'rxjs';
import { debounceTime, distinctUntilChanged, catchError, finalize } from 'rxjs/operators';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';
import { VechicleService } from 'app/modules/components/transport/vechicle/vehicle.service';

@Injectable()
export class VehicleSelectorModalService {
  private readonly vehicleService = inject(VechicleService);

  // === ACTIONS (RxJS Subjects) ===
  public readonly setSearch$ = new Subject<string>();
  public readonly setPage$ = new Subject<{ pageIndex: number; pageSize: number }>();
  public readonly toggleVehicle$ = new Subject<VehicleAttributes>();
  public readonly toggleAllPage$ = new Subject<VehicleAttributes[]>();
  public readonly clearSelection$ = new Subject<void>();
  public readonly retry$ = new Subject<void>();

  // === STATE (Signals) ===
  private readonly searchTerm = signal<string>('');
  private readonly currentPage = signal<number>(1);
  private readonly pageSize = signal<number>(10);
  private readonly vehiclesData = signal<VehicleAttributes[]>([]);
  private readonly totalItems = signal<number>(0);
  private readonly loading = signal<boolean>(false);
  private readonly errorState = signal<Error | null>(null);

  // Selección persistente (Map para acceso O(1))
  private readonly selectedVehiclesMap = signal<Map<string, VehicleAttributes>>(new Map());

  // === COMPUTED (Public API) ===
  public readonly vehicles = this.vehiclesData.asReadonly();
  public readonly total = this.totalItems.asReadonly();
  public readonly isLoading = this.loading.asReadonly();
  public readonly error = this.errorState.asReadonly();
  public readonly page = this.currentPage.asReadonly();
  public readonly size = this.pageSize.asReadonly();

  // Selección
  public readonly selectedVehicles = computed(() =>
    Array.from(this.selectedVehiclesMap().values())
  );
  public readonly selectedIds = computed(() =>
    Array.from(this.selectedVehiclesMap().keys())
  );
  public readonly selectedCount = computed(() =>
    this.selectedVehiclesMap().size
  );

  // Verificar si un vehículo está seleccionado
  public isSelected(vehicleId: string | unknown): boolean {
    return this.selectedVehiclesMap().has(String(vehicleId));
  }

  // Verificar si todos los de la página están seleccionados
  public readonly allPageSelected = computed(() => {
    const vehicles = this.vehiclesData();
    if (vehicles.length === 0) return false;
    return vehicles.every(v => this.selectedVehiclesMap().has(String(v._id)));
  });

  public readonly somePageSelected = computed(() => {
    const vehicles = this.vehiclesData();
    const selected = this.selectedVehiclesMap();
    const someSelected = vehicles.some(v => selected.has(String(v._id)));
    const allSelected = vehicles.every(v => selected.has(String(v._id)));
    return someSelected && !allSelected;
  });

  constructor() {
    this.setupSubscriptions();
    this.loadVehicles();
  }

  /** Inicializa la selección con vehículos pre-seleccionados */
  public initializeSelection(vehicles: VehicleAttributes[]): void {
    const map = new Map<string, VehicleAttributes>();
    vehicles.forEach(v => map.set(String(v._id), v));
    this.selectedVehiclesMap.set(map);
  }

  /** Actualiza la selección desde el SmartGrid */
  public setSelectedVehicles(vehicles: VehicleAttributes[]): void {
    const map = new Map<string, VehicleAttributes>();
    vehicles.forEach(v => map.set(String(v._id), v));
    this.selectedVehiclesMap.set(map);
  }

  private setupSubscriptions(): void {
    this.setSearch$.pipe(
      takeUntilDestroyed(),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(search => {
      this.searchTerm.set(search);
      this.currentPage.set(1);
      this.loadVehicles();
    });

    this.setPage$.pipe(takeUntilDestroyed())
      .subscribe(({ pageIndex, pageSize }) => {
        this.currentPage.set(pageIndex + 1);
        this.pageSize.set(pageSize);
        this.loadVehicles();
      });

    this.toggleVehicle$.pipe(takeUntilDestroyed())
      .subscribe(vehicle => this.toggleVehicleSelection(vehicle));

    this.toggleAllPage$.pipe(takeUntilDestroyed())
      .subscribe(vehicles => this.toggleAllPageSelection(vehicles));

    this.clearSelection$.pipe(takeUntilDestroyed())
      .subscribe(() => this.selectedVehiclesMap.set(new Map()));

    this.retry$.pipe(takeUntilDestroyed())
      .subscribe(() => this.loadVehicles());
  }

  private toggleVehicleSelection(vehicle: VehicleAttributes): void {
    const newMap = new Map(this.selectedVehiclesMap());
    const id = String(vehicle._id);
    if (newMap.has(id)) {
      newMap.delete(id);
    } else {
      newMap.set(id, vehicle);
    }
    this.selectedVehiclesMap.set(newMap);
  }

  private toggleAllPageSelection(vehicles: VehicleAttributes[]): void {
    const newMap = new Map(this.selectedVehiclesMap());
    const allSelected = vehicles.every(v => newMap.has(String(v._id)));

    if (allSelected) {
      // Deseleccionar todos de la página
      vehicles.forEach(v => newMap.delete(String(v._id)));
    } else {
      // Seleccionar todos de la página
      vehicles.forEach(v => newMap.set(String(v._id), v));
    }
    this.selectedVehiclesMap.set(newMap);
  }

  private loadVehicles(): void {
    this.loading.set(true);
    this.errorState.set(null);

    this.vehicleService.getAllPaginateVehicle({
      search: this.searchTerm() || undefined,
      currentPage: this.currentPage(),
      limitPerPage: this.pageSize(),
      sort: 'name',
      sortDirection: 'ASC'
    }).pipe(
      catchError(err => {
        this.errorState.set(err);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false))
    ).subscribe(response => {
      const data = response?.data?.[0];
      this.vehiclesData.set(data?.data ?? []);
      this.totalItems.set(data?.pageInfo?.[0]?.total ?? 0);
    });
  }
}
